import { Dispatch, SetStateAction } from 'react'

import {
  MEDIA_ANALYSIS_HISTORY_CONDITION_CLEAR,
  MediaAnalysisHistoryCondition,
  useAnalysisTypesApi
} from '@/common/api/modules/mediaAnalysis'
import { isSameSearchCondition } from '@/common/api/search'
import { ANALYSIS_STATUSES, analysisStatusLabel } from '@/common/app/modules/media/mediaAnalysis'
import { useSiteId } from '@/common/app/site'

import Button from '@/components/view/atoms/button/Button'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import SearchConditionAreaContent from '@/components/view/layouts/area/SearchConditionAreaContent'
import SortButtonList from '@/components/view/layouts/button/SortButtonList'
import FormRow from '@/components/view/layouts/form/FormRow'
import DisclosureSortDateBetweenButton from '@/components/view/molecules/disclosure/DisclosureSortDateBetweenButton'
import DisclosureSortRadioSelect from '@/components/view/molecules/disclosure/DisclosureSortRadioSelect'

interface Props {
  condition: MediaAnalysisHistoryCondition
  setCondition: Dispatch<SetStateAction<MediaAnalysisHistoryCondition>>
}

const MediaAnalysisHistorySearchConditionField = ({ condition, setCondition }: Props) => {
  const siteId = useSiteId()
  const { data: types } = useAnalysisTypesApi({ site_id: siteId })

  const isDefault = isSameSearchCondition(condition, MEDIA_ANALYSIS_HISTORY_CONDITION_CLEAR)
  const dateQuantity = [condition.dateFrom, condition.dateTo].filter(Boolean).length

  return (
    <SearchConditionAreaContent>
      <FormRow gap={8} size={TextBoxSize.FULL} align='center'>
        <TextField
          type='search'
          size={TextBoxSize.FULL}
          value={condition.q ?? ''}
          onChange={(e) => setCondition((c) => ({ ...c, page: 1, q: e.target.value || null }))}
          placeholder='分析名で検索...'
        />
      </FormRow>
      <SortButtonList>
        <DisclosureSortRadioSelect
          name='type'
          currentValue={condition.type}
          choices={[
            { name: 'すべて', value: null },
            ...(types ?? []).map((t) => ({ name: t.label, value: t.key }))
          ]}
          setCondition={setCondition}
        >
          種別
        </DisclosureSortRadioSelect>
        <DisclosureSortDateBetweenButton
          quantity={dateQuantity}
          defaultStartValue={condition.dateFrom}
          defaultEndValue={condition.dateTo}
          onComplete={(start, end) =>
            setCondition((c) => ({ ...c, page: 1, dateFrom: start || null, dateTo: end || null }))
          }
        >
          実行日時
        </DisclosureSortDateBetweenButton>
        <DisclosureSortRadioSelect
          name='status'
          currentValue={condition.status}
          choices={[
            { name: 'すべて', value: null },
            ...ANALYSIS_STATUSES.map((s) => ({ name: analysisStatusLabel(s), value: s }))
          ]}
          setCondition={setCondition}
        >
          状態
        </DisclosureSortRadioSelect>
        <DisclosureSortRadioSelect
          name='executionSource'
          currentValue={condition.executionSource}
          choices={[
            { name: 'すべて', value: null },
            { name: '手動', value: 'manual' },
            { name: '自動', value: 'scheduled' }
          ]}
          setCondition={setCondition}
        >
          実行方法
        </DisclosureSortRadioSelect>
        <Button
          color='secondary'
          disabled={isDefault}
          onClick={() => setCondition(MEDIA_ANALYSIS_HISTORY_CONDITION_CLEAR)}
        >
          条件をリセット
        </Button>
      </SortButtonList>
    </SearchConditionAreaContent>
  )
}

export default MediaAnalysisHistorySearchConditionField
